/**
 * LinkActionCard - Content for CRM association add/remove actions.
 *
 * Renders LinkedPair showing source and target entities using the rich
 * ContentComponent from ENTITY_CONTENT_REGISTRY (e.g. ContactCardContent)
 * wrapped in CrmClickableContent so each name opens the slideout — same
 * pattern as Create/Update/HiddenEntity/Delete/StageAssociation cards.
 * Unknown entity types fall back to the generic EntityCard.
 *
 * Shell is provided by the parent. Read-only.
 */

import { cx } from '@/utils/cx';

import type {
  AssociationAddedEntityPayload,
  AssociationRemovedEntityPayload,
  ProposedAction,
} from '../../lib/proposals/core/types';
import { isAssociationPayload } from '../../lib/proposals/core/types';
import type { ActionRendererProps } from '../../lib/proposals/registry/types';
import { ActionEntityPreview } from './ActionEntityPreview';
import { LinkedPair } from './LinkedPair';
import {
  ActionError,
  buildEnrichedEntityData,
  getEntityTypeLabel,
  parseAssociationType,
} from './shared';
import { ACTION_CARD_BODY_CLASS } from './shared/actionCardLayout';

// ─── Association description templates ──────────────────────────────────────

/**
 * Human-readable relationship verbs for known association types.
 * Keys are association_type_name values; values are [addVerb, removeVerb]
 * templates where {source} and {target} are replaced at render time.
 *
 * "add" verb is used for ahoy_association_added,
 * "remove" verb is used for ahoy_association_removed.
 */
const ASSOCIATION_DESCRIPTIONS: Record<string, [add: string, remove: string]> =
  {
    ahoy_contact_to_company: [
      'Set the company for {source} to {target}',
      'Remove {source} from {target}',
    ],
    ahoy_deal_to_contact: [
      'Link {target} as the primary contact on {source}',
      'Remove {target} as the primary contact on {source}',
    ],
    ahoy_deal_to_company: [
      'Associate {source} with {target}',
      'Remove {source} from {target}',
    ],
    ahoy_deal_to_owner: [
      'Assign {target} as the owner of {source}',
      'Remove {target} as the owner of {source}',
    ],
    ahoy_contact_to_owner: [
      'Assign {target} as the owner of {source}',
      'Remove {target} as the owner of {source}',
    ],
    ahoy_company_to_owner: [
      'Assign {target} as the owner of {source}',
      'Remove {target} as the owner of {source}',
    ],
    ahoy_company_to_contact: [
      'Set the company for {target} to {source}',
      'Remove {target} from {source}',
    ],
  };

/**
 * Build a human-readable one-liner for an association action.
 * Uses built-in templates for known types only when the actual source entity
 * matches the association's declared source type. A converted task riding a
 * deal edge (e.g. ahoy_deal_to_contact with a task source) must NOT get
 * deal-flavoured copy ("...as the primary contact on...").
 * Falls back to generic copy for mismatched or unknown types.
 */
function buildAssociationDescription(
  associationTypeName: string,
  sourceLabel: string,
  targetLabel: string,
  isRemoval: boolean,
  sourceEntityType: string
): string {
  const templates = ASSOCIATION_DESCRIPTIONS[associationTypeName];
  const declaredSource = parseAssociationType(associationTypeName).source;
  // Only use the type-specific template when the ACTUAL source entity matches
  // the association's declared source type.
  if (templates && sourceEntityType === `ahoy_${declaredSource}`) {
    const template = isRemoval ? templates[1] : templates[0];
    return template
      .replace('{source}', sourceLabel)
      .replace('{target}', targetLabel);
  }

  // Fallback: generic, type-neutral copy
  if (isRemoval) {
    return `Remove the link between ${sourceLabel} and ${targetLabel}`;
  }
  return `Link ${sourceLabel} to ${targetLabel}`;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function LinkActionCard({
  action,
  mode,
  enrichedData,
  orgId,
}: ActionRendererProps) {
  if (!isAssociationPayload(action.payload)) {
    return <ActionError message='Invalid association payload' mode={mode} />;
  }

  const typedAction = action as ProposedAction<
    AssociationAddedEntityPayload | AssociationRemovedEntityPayload
  >;
  const { entity_id, entity_type_name, event_type_name, payload } =
    typedAction.payload;
  const { association_type_name, target_entity_id } = payload;

  const { source: sourceType, target: targetType } = parseAssociationType(
    association_type_name
  );
  if (sourceType.endsWith('_timeline') || targetType.endsWith('_timeline')) {
    return (
      <ActionError
        message={`Invalid association type: ${association_type_name} (_timeline identifiers are not valid association types)`}
        mode={mode}
      />
    );
  }

  const isRemoval = event_type_name === 'ahoy_association_removed';

  // Fallback labels use only the entity type — never a raw/truncated
  // entity id.
  const sourceLabel =
    enrichedData?.object_label || getEntityTypeLabel(sourceType);
  const sourceEntityType = enrichedData?.entity_type_name || entity_type_name;

  const targetLabel =
    enrichedData?.target_object?.label || getEntityTypeLabel(targetType);
  const targetEntityType =
    enrichedData?.target_object?.entity_type_name ||
    (targetType.startsWith('ahoy_') ? targetType : `ahoy_${targetType}`);

  const sourceEntityId = entity_id;
  const targetEntityId = target_entity_id;

  // Synthesize enrichedData for the target side — ContentComponent prop
  // builders (e.g. buildContactContentProps) use object_label/entity_type_name
  // as fallbacks and target_object carries only those fields.
  const targetEnrichedData = buildEnrichedEntityData(
    enrichedData?.target_object,
    targetLabel,
    targetEntityType
  );

  const description = buildAssociationDescription(
    association_type_name,
    sourceLabel,
    targetLabel,
    isRemoval,
    sourceEntityType
  );

  return (
    <div className={cx(ACTION_CARD_BODY_CLASS, 'space-y-2')}>
      <p className='text-sm text-fg-secondary'>{description}</p>
      <LinkedPair
        source={
          <ActionEntityPreview
            entityId={sourceEntityId}
            entityType={sourceEntityType}
            label={sourceLabel}
            enrichedData={enrichedData}
            orgId={orgId}
            compact
          />
        }
        target={
          <ActionEntityPreview
            entityId={targetEntityId}
            entityType={targetEntityType}
            label={targetLabel}
            enrichedData={targetEnrichedData}
            orgId={orgId}
            compact
          />
        }
        direction='source-to-target'
        isRemoval={isRemoval}
      />
    </div>
  );
}
